import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { ShoppingCart, Plus, Trash2, CheckCircle, Clock, User, Phone, Wrench } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrdersManager = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    emri_plot: "",
    telefoni: "",
    pershkrimi: "",
    status: "ne_proces"
  });
  const [newOrdersCount, setNewOrdersCount] = useState(0);
  const [editingOrder, setEditingOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
    // Poll for new orders every 30 seconds
    const interval = setInterval(fetchOrders, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error("Fetch orders error:", error);
      toast.error("Gabim në ngarkimin e porosive");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.emri_plot) {
      toast.error("Emri i plotë është i detyrueshëm");
      return;
    }

    try {
      if (editingOrder) {
        await axios.put(`${API}/orders/${editingOrder.id}`, formData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success("Porosi u përditësua me sukses");
      } else {
        await axios.post(`${API}/orders`, formData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success("Porosi u regjistrua me sukses");
      }

      setIsModalOpen(false);
      setEditingOrder(null);
      setFormData({ emri_plot: "", telefoni: "", pershkrimi: "", status: "ne_proces" });
      fetchOrders();
    } catch (error) {
      toast.error("Gabim në ruajtjen e porosisë");
      console.error("Save order error:", error);
    }
  };

  const handleEdit = (order) => {
    setEditingOrder(order);
    setFormData({
      emri_plot: order.emri_plot || `${order.emri || ''} ${order.mbiemri || ''}`.trim(),
      telefoni: order.telefoni || "",
      pershkrimi: order.pershkrimi || "",
      status: order.status
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("A jeni të sigurt që dëshironi të fshini këtë porosi?")) {
      return;
    }

    try {
      await axios.delete(`${API}/orders/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success("Porosi u fshi me sukses");
      fetchOrders();
    } catch (error) {
      toast.error("Gabim në fshirjen e porosisë");
      console.error("Delete order error:", error);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.put(`${API}/orders/${id}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success("Statusi u përditësua");
      fetchOrders();
    } catch (error) {
      toast.error("Gabim në përditësimin e statusit");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center text-2xl">
                <ShoppingCart className="w-7 h-7 mr-2 text-blue-600" />
                Menaxhimi i Porosive
              </CardTitle>
              <CardDescription>
                Merre shënime për porosi të ndryshme të klientëve
              </CardDescription>
            </div>
            <Button onClick={() => {
              setEditingOrder(null);
              setFormData({ emri_plot: "", telefoni: "", pershkrimi: "", status: "ne_proces" });
              setIsModalOpen(true);
            }}>
              <Plus className="w-4 h-4 mr-2" />
              Porosi e Re
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {orders.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <ShoppingCart className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p className="text-lg">Nuk ka porosi të regjistruara.</p>
              <p className="text-sm">Kliko "Porosi e Re" për të shtuar.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {orders.map((order) => (
                <div
                  key={order.id}
                  className={`border-l-4 rounded-lg p-4 hover:shadow-lg transition ${
                    order.status === 'perfunduar' ? 'border-green-500 bg-green-50' :
                    order.status === 'ne_proces' ? 'border-blue-500 bg-blue-50' :
                    'border-yellow-500 bg-yellow-50'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      {/* Header */}
                      <div className="flex items-center space-x-3 mb-2">
                        <User className="w-5 h-5 text-blue-600" />
                        <h3 className="text-lg font-semibold text-slate-900">
                          {order.emri_plot || `${order.emri || ''} ${order.mbiemri || ''}`.trim() || 'Pa emër'}
                        </h3>
                        <Badge 
                          variant={
                            order.status === 'perfunduar' ? 'default' : 
                            order.status === 'ne_proces' ? 'secondary' : 
                            'outline'
                          }
                          className={
                            order.status === 'perfunduar' ? 'bg-green-500 text-white' :
                            order.status === 'ne_proces' ? 'bg-blue-500 text-white' :
                            'bg-yellow-100 text-yellow-800'
                          }
                        >
                          {order.status === 'perfunduar' ? (
                            <>
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Përfunduar
                            </>
                          ) : order.status === 'ne_proces' ? (
                            <>
                              <Wrench className="w-3 h-3 mr-1" />
                              Në Proces
                            </>
                          ) : (
                            <>
                              <Clock className="w-3 h-3 mr-1" />
                              Në pritje
                            </>
                          )}
                        </Badge>
                      </div>

                      {/* Contact */}
                      <div className="flex items-center space-x-2 mb-3">
                        <Phone className="w-4 h-4 text-slate-500" />
                        <span className="text-sm text-slate-600">{order.telefoni}</span>
                      </div>

                      {/* Description */}
                      <div className="bg-slate-50 rounded p-3">
                        <span className="text-xs font-medium text-slate-600 block mb-1">Përshkrimi i Porosisë:</span>
                        <p className="text-sm text-slate-800">{order.pershkrimi}</p>
                      </div>

                      {/* Date */}
                      {order.created_at && (
                        <p className="text-xs text-slate-500 mt-2">
                          Regjistruar: {new Date(order.created_at).toLocaleString('sq-AL')}
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col space-y-2 ml-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(order)}
                      >
                        ✏️ Edit
                      </Button>
                      
                      {/* Simple status buttons */}
                      {order.status !== 'ne_proces' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="bg-blue-50 text-blue-700 hover:bg-blue-100"
                          onClick={() => handleStatusChange(order.id, 'ne_proces')}
                        >
                          <Wrench className="w-4 h-4 mr-1" />
                          Në Proces
                        </Button>
                      )}
                      
                      {order.status !== 'perfunduar' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="bg-green-50 text-green-700 hover:bg-green-100"
                          onClick={() => handleStatusChange(order.id, 'perfunduar')}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Përfunduar
                        </Button>
                      )}
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(order.id)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>
                {editingOrder ? "Përditëso Porosinë" : "Porosi e Re"}
              </CardTitle>
              <CardDescription>
                Plotëso të dhënat për porosinë
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="emri_plot">Emri i Plotë *</Label>
                  <Input
                    id="emri_plot"
                    value={formData.emri_plot}
                    onChange={(e) => setFormData({ ...formData, emri_plot: e.target.value })}
                    placeholder="Emri dhe Mbiemri"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="telefoni">Telefoni (opsional)</Label>
                  <Input
                    id="telefoni"
                    type="tel"
                    value={formData.telefoni}
                    onChange={(e) => setFormData({ ...formData, telefoni: e.target.value })}
                    placeholder="+383 XX XXX XXX"
                  />
                </div>

                <div>
                  <Label htmlFor="pershkrimi">Përshkrimi i Porosisë (opsional)</Label>
                  <Textarea
                    id="pershkrimi"
                    value={formData.pershkrimi}
                    onChange={(e) => setFormData({ ...formData, pershkrimi: e.target.value })}
                    placeholder="Përshkruani porosinë në detaje..."
                    rows={4}
                  />
                </div>

                <div className="flex justify-end space-x-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsModalOpen(false);
                      setEditingOrder(null);
                      setFormData({ emri_plot: "", telefoni: "", pershkrimi: "", status: "ne_proces" });
                    }}
                  >
                    Anulo
                  </Button>
                  <Button type="submit">
                    {editingOrder ? "Përditëso" : "Ruaj"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default OrdersManager;
